import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import StatusBadge from '../../components/StatusBadge';
import Modal from '../../components/Modal';
import Toast from '../../components/Toast';

const GPS_MIN_READINGS = 6;
const GPS_MAX_READINGS = 10;
const GPS_WINDOW_MS = 45000;
const GPS_MAX_ACCURACY_M = 10;

const fmtDate = (d) => (d ? d.substring(0, 10).split('-').reverse().join('/') : '—');

const median = (arr) => {
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

const capturarGpsMulti = (onProgress) => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('Este dispositivo não oferece suporte à localização.'));
    return;
  }
  const readings = [];
  let done = false;
  let watchId = null;
  let timer = null;

  const finish = () => {
    if (done) return;
    done = true;
    if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    if (timer) clearTimeout(timer);
    if (readings.length === 0) {
      reject(new Error('Nenhuma leitura de localização recebida — verifique o sinal e tente novamente.'));
      return;
    }
    resolve({
      latitude: median(readings.map((r) => r.latitude)),
      longitude: median(readings.map((r) => r.longitude)),
      accuracy: median(readings.map((r) => r.accuracy)),
      count: readings.length,
      at: new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
    });
  };

  const onPos = (pos) => {
    if (done) return;
    readings.push({
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
    });
    if (onProgress) onProgress(readings.length, pos.coords.accuracy);
    if (readings.length >= GPS_MAX_READINGS) finish();
  };

  const onErr = (err) => {
    if (done) return;
    if (readings.length > 0) { finish(); return; }
    done = true;
    if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    if (timer) clearTimeout(timer);
    reject(new Error(
      err.code === 1
        ? 'Permissão de localização negada. Libere o acesso à localização nas permissões do navegador.'
        : err.code === 3
          ? 'Tempo esgotado aguardando o sinal — tente novamente ao ar livre.'
          : 'Não foi possível obter a localização. Verifique se o GPS está ativo.'
    ));
  };

  watchId = navigator.geolocation.watchPosition(onPos, onErr, {
    enableHighAccuracy: true, timeout: 20000, maximumAge: 0,
  });
  timer = setTimeout(() => {
    if (readings.length < GPS_MIN_READINGS) {
      timer = setTimeout(finish, 30000);
      if (onProgress) onProgress(readings.length, readings.length ? readings[readings.length - 1].accuracy : null, true);
      return;
    }
    finish();
  }, GPS_WINDOW_MS);
});

const fmtDateTime = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

export default function Graves() {
  const [graves, setGraves] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ block: '', sector: '', search: '', field: 'all' });
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [searchInput, setSearchInput] = useState('');
  const [options, setOptions] = useState({ blocks: [], sectors: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [gpsTarget, setGpsTarget] = useState(null);
  const [gpsFix, setGpsFix] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsProgress, setGpsProgress] = useState(null);
  const [gpsError, setGpsError] = useState('');
  const [gpsForce, setGpsForce] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ block: '', sector: '', number: '', x_position: 0, y_position: 0, status: 'livre' });
  const firstRun = useRef(true);

  const loadGraves = () => {
    setLoading(true);
    api.get('/admin/graves', { params: { ...filters, sort: sort.key, dir: sort.dir } })
      .then(({ data, headers }) => {
        setGraves(data);
        setTotal(Number(headers['x-total-count'] || data.length));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    Promise.all([
      api.get('/admin/graves'),
      api.get('/admin/graves/options'),
    ])
      .then(([gravesRes, optsRes]) => {
        setGraves(gravesRes.data);
        setTotal(Number(gravesRes.headers['x-total-count'] || gravesRes.data.length));
        setLoading(false);
        setOptions(optsRes.data);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    loadGraves();
  }, [filters, sort]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleNew = () => {
    setEditing(null);
    setForm({
      block: '', sector: '', number: '', x_position: 50, y_position: 50, status: 'livre',
      owner_name: '', contract: '', deceased_name: '', death_date: '', deceased_obs: '',
    });
    setModalOpen(true);
  };

  const handleEdit = (grave) => {
    setEditing(grave);
    setForm({
      block: grave.block,
      sector: grave.sector,
      number: grave.number,
      x_position: parseFloat(grave.x_position),
      y_position: parseFloat(grave.y_position),
      status: grave.status,
      owner_name: grave.owner_name || '',
      contract: grave.contract || '',
      deceased_name: grave.deceased_name || '',
      death_date: grave.deceased_death_date ? grave.deceased_death_date.substring(0, 10) : '',
      deceased_obs: grave.deceased_obs || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/admin/graves/${editing.id}`, form);
        setToast({ message: 'Jazigo atualizado com sucesso' });
      } else {
        await api.post('/admin/graves', form);
        setToast({ message: 'Jazigo criado com sucesso' });
      }
      setModalOpen(false);
      loadGraves();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Erro ao salvar', type: 'error' });
    }
  };

  const handleDelete = async (grave) => {
    if (!window.confirm(`Excluir jazigo ${grave.number}?`)) return;
    try {
      await api.delete(`/admin/graves/${grave.id}`);
      setToast({ message: 'Jazigo excluído' });
      loadGraves();
    } catch (err) {
      setToast({ message: 'Erro ao excluir', type: 'error' });
    }
  };

  const closeGps = () => {
    setGpsTarget(null);
    setGpsFix(null);
    setGpsError('');
    setGpsProgress(null);
    setGpsForce(false);
  };

  const openGps = (g) => {
    setGpsFix(null);
    setGpsError('');
    setGpsProgress(null);
    setGpsForce(false);
    setGpsTarget(g);
  };

  const capturarGps = async () => {
    setGpsBusy(true);
    setGpsError('');
    setGpsFix(null);
    setGpsForce(false);
    setGpsProgress({ count: 0, accuracy: null });
    try {
      const fix = await capturarGpsMulti((count, accuracy, extended) => {
        setGpsProgress({ count, accuracy, extended });
      });
      setGpsFix(fix);
    } catch (err) {
      setGpsError(err.message);
    } finally {
      setGpsBusy(false);
      setGpsProgress(null);
    }
  };

  const confirmarGps = async () => {
    if (!gpsFix || !gpsTarget) return;
    const lowAcc = gpsFix.accuracy > GPS_MAX_ACCURACY_M;
    if (lowAcc && !gpsForce) {
      setGpsError(`Precisão de ${Math.round(gpsFix.accuracy)} m acima do limite de ${GPS_MAX_ACCURACY_M} m. Recapture ao ar livre ou marque "forçar salvamento".`);
      return;
    }
    try {
      const { data } = await api.put(`/admin/graves/${gpsTarget.id}/location`, {
        latitude: gpsFix.latitude,
        longitude: gpsFix.longitude,
        accuracy: gpsFix.accuracy,
        force: lowAcc,
      });
      setGraves((list) => list.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
      setGpsTarget((t) => (t ? { ...t, ...data } : t));
      setGpsFix(null);
      setGpsForce(false);
      setToast({
        message: lowAcc
          ? `Salvo com BAIXA precisão (${Math.round(gpsFix.accuracy)} m) — confira no mapa antes de usar`
          : `Localização salva — precisão de ${Math.round(gpsFix.accuracy)} m (${gpsFix.count} leituras)`,
        type: lowAcc ? 'error' : undefined,
      });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Erro ao salvar localização', type: 'error' });
    }
  };

  const limparGps = async () => {
    if (!gpsTarget) return;
    if (!window.confirm('Limpar a localização GPS deste jazigo?')) return;
    try {
      const { data } = await api.delete(`/admin/graves/${gpsTarget.id}/location`);
      setGraves((list) => list.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
      setGpsTarget((t) => (t ? { ...t, ...data } : t));
      setToast({ message: 'Localização removida' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Erro ao remover localização', type: 'error' });
    }
  };

  const gpsNext = () => {
    if (!gpsTarget) return;
    const i = graves.findIndex((x) => x.id === gpsTarget.id);
    if (i >= 0 && i < graves.length - 1) {
      setGpsFix(null);
      setGpsError('');
      setGpsProgress(null);
      setGpsForce(false);
      setGpsTarget(graves[i + 1]);
    }
  };

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  const sortableTh = (key, label) => (
    <th
      className={`sortable${sort.key === key ? ' active' : ''}`}
      onClick={() => toggleSort(key)}
      title={`Ordenar por ${label}`}
    >
      {label}{' '}
      <span className="sort-arrow">{sort.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  );

  const shown = graves.slice(0, 500);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Cadastro de Jazigos</h1>
          <p>Gerencie os jazigos do cemitério.</p>
        </div>
        <button className="btn-primary" onClick={handleNew}>+ NOVO JAZIGO</button>
      </div>

      <div className="filters-bar">
        <select
          className="form-select"
          value={filters.block}
          onChange={(e) => setFilters({ ...filters, block: e.target.value })}
        >
          <option value="">Quadra (todas)</option>
          {options.blocks.map(b => <option key={b} value={b}>Quadra {b}</option>)}
        </select>
        <select
          className="form-select"
          value={filters.sector}
          onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
        >
          <option value="">Setor (todos)</option>
          {options.sectors.map(s => <option key={s} value={s}>Setor {s}</option>)}
        </select>
        <select
          className="form-select"
          value={filters.field}
          onChange={(e) => setFilters({ ...filters, field: e.target.value })}
        >
          <option value="all">Filtro: Tudo</option>
          <option value="contract">Filtro: Contrato</option>
          <option value="number">Filtro: Jazigo</option>
          <option value="name">Filtro: Nome</option>
        </select>
        <input
          type="text"
          className="form-input search-field"
          placeholder={
            filters.field === 'contract' ? 'Nº do contrato...'
            : filters.field === 'number' ? 'Nº do jazigo...'
            : filters.field === 'name' ? 'Nome do proprietário ou sepultado...'
            : 'Contrato, nº do jazigo ou nome...'
          }
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              {sortableTh('quadra', 'Quadra')}
              {sortableTh('setor', 'Setor')}
              {sortableTh('numero', 'Número')}
              {sortableTh('contrato', 'Contrato')}
              {sortableTh('proprietario', 'Proprietário')}
              {sortableTh('status', 'Status')}
              {sortableTh('gps', 'GPS')}
              {sortableTh('sepultado', 'Sepultado')}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" style={{ textAlign: 'center', padding: 40 }}>Carregando...</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan="9" style={{ textAlign: 'center', padding: 40, color: 'var(--texto-secundario)' }}>Nenhum jazigo encontrado.</td></tr>
            ) : shown.map((g) => (
              <tr key={g.id}>
                <td><strong>{g.block}</strong></td>
                <td>{g.sector || '—'}</td>
                <td>{g.number}</td>
                <td>{g.contract || '—'}</td>
                <td>{g.owner_name || '—'}</td>
                <td><StatusBadge status={g.status} /></td>
                <td>
                  {g.latitude != null ? (
                    <span
                      className={`gps-badge ${g.location_status === 'baixa_precisao' ? 'warn' : 'ok'}`}
                      title={`Capturado em ${fmtDateTime(g.captured_at)}${g.gps_accuracy_m != null ? ` — precisão ${Math.round(g.gps_accuracy_m)} m` : ''}`}
                    >
                      {g.location_status === 'baixa_precisao' ? '⚠ GPS' : '✓ GPS'}
                    </span>
                  ) : (
                    <span className="gps-badge none">—</span>
                  )}
                </td>
                <td>
                  {g.deceased && g.deceased.length
                    ? g.deceased.map((p) => p.name).join(', ')
                    : g.deceased_name || '—'}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: 12, marginRight: 6 }} onClick={() => setViewing(g)}>Visualizar</button>
                  <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: 12, marginRight: 6 }} onClick={() => openGps(g)}>GPS</button>
                  <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: 12, marginRight: 6 }} onClick={() => handleEdit(g)}>Editar</button>
                  <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: 12, color: 'var(--erro)', borderColor: '#E5B8B8' }} onClick={() => handleDelete(g)}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > shown.length && (
          <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--texto-secundario)', textAlign: 'center' }}>
            Mostrando {shown.length} de {total} jazigos — use os filtros para refinar.
          </div>
        )}
      </div>

      {modalOpen && (
        <Modal
          title={editing ? 'Editar Jazigo' : 'Novo Jazigo'}
          onClose={() => setModalOpen(false)}
          footer={
            <>
              <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleSave}>Salvar</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Quadra</label>
            <input type="text" className="form-input" value={form.block} onChange={(e) => setForm({ ...form, block: e.target.value })} placeholder="Ex: 04" />
          </div>
          <div className="form-group">
            <label className="form-label">Setor</label>
            <input type="text" className="form-input" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} placeholder="Ex: B" />
          </div>
          <div className="form-group">
            <label className="form-label">Número do jazigo</label>
            <input type="text" className="form-input" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="Ex: 127" />
          </div>
          <div className="form-group">
            <label className="form-label">Proprietário</label>
            <input type="text" className="form-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Nome do proprietário" />
          </div>
          <div className="form-group">
            <label className="form-label">Nº de Contrato</label>
            <input type="text" className="form-input" value={form.contract} onChange={(e) => setForm({ ...form, contract: e.target.value })} placeholder="Ex: 1234" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Coordenada X (%)</label>
              <input type="number" className="form-input" value={form.x_position} onChange={(e) => setForm({ ...form, x_position: parseFloat(e.target.value) || 0 })} min="0" max="100" step="0.1" />
            </div>
            <div className="form-group">
              <label className="form-label">Coordenada Y (%)</label>
              <input type="number" className="form-input" value={form.y_position} onChange={(e) => setForm({ ...form, y_position: parseFloat(e.target.value) || 0 })} min="0" max="100" step="0.1" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="livre">Livre</option>
              <option value="ocupado">Ocupado</option>
            </select>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--borda)', margin: '20px 0' }} />
          <div className="form-group">
            <label className="form-label">Sepultado</label>
            <input type="text" className="form-input" value={form.deceased_name} onChange={(e) => setForm({ ...form, deceased_name: e.target.value })} placeholder="Nome da pessoa sepultada" />
          </div>
          <div className="form-group">
            <label className="form-label">Data de Falecimento</label>
            <input type="date" className="form-input" value={form.death_date} onChange={(e) => setForm({ ...form, death_date: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Obs</label>
            <textarea className="form-input" rows="3" value={form.deceased_obs} onChange={(e) => setForm({ ...form, deceased_obs: e.target.value })} placeholder="Ex: 2ª gaveta, exumado..." style={{ resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </Modal>
      )}

      {gpsTarget && (
        <Modal
          title={`Cadastrar localização — ${gpsTarget.block}/${gpsTarget.sector || '—'}/${gpsTarget.number}`}
          onClose={closeGps}
          footer={
            <>
              <button className="btn-secondary" onClick={closeGps}>Fechar</button>
              <button
                className="btn-secondary"
                onClick={gpsNext}
                disabled={graves.findIndex((x) => x.id === gpsTarget.id) >= graves.length - 1}
              >
                Próximo ▸
              </button>
              <button className="btn-primary" onClick={confirmarGps} disabled={!gpsFix}>
                Confirmar e salvar
              </button>
            </>
          }
        >
          <div className="detail-grid">
            <div className="detail-item">
              <label>Quadra</label>
              <strong>{gpsTarget.block}</strong>
            </div>
            <div className="detail-item">
              <label>Setor</label>
              <strong>{gpsTarget.sector || '—'}</strong>
            </div>
            <div className="detail-item">
              <label>Número</label>
              <strong>{gpsTarget.number}</strong>
            </div>
            <div className="detail-item">
              <label>Contrato</label>
              <strong>{gpsTarget.contract || '—'}</strong>
            </div>
          </div>

          {gpsTarget.location_status && (
            <div className="gps-saved">
              <div className="gps-saved-head">
                <span className={`gps-badge ${gpsTarget.location_status === 'confirmado' ? 'ok' : 'warn'}`}>
                  {gpsTarget.location_status === 'confirmado' ? '✓ Localização salva' : '⚠ Salva com baixa precisão'}
                </span>
                <span className="gps-saved-meta">
                  Capturado em {fmtDateTime(gpsTarget.captured_at)}
                  {gpsTarget.captured_by_email ? ` — ${gpsTarget.captured_by_email}` : ''}
                </span>
              </div>
              <div className="detail-grid" style={{ marginTop: 12 }}>
                <div className="detail-item">
                  <label>Latitude</label>
                  <strong>{Number(gpsTarget.latitude).toFixed(6)}</strong>
                </div>
                <div className="detail-item">
                  <label>Longitude</label>
                  <strong>{Number(gpsTarget.longitude).toFixed(6)}</strong>
                </div>
                <div className="detail-item">
                  <label>Precisão</label>
                  <strong>{gpsTarget.gps_accuracy_m != null ? `${Math.round(gpsTarget.gps_accuracy_m)} m` : '—'}</strong>
                </div>
                <div className="detail-item">
                  <label>Ações</label>
                  <a
                    href={`https://maps.google.com/?q=${gpsTarget.latitude},${gpsTarget.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Abrir no mapa ↗
                  </a>
                </div>
              </div>
              <button className="gps-clear" onClick={limparGps}>Limpar localização</button>
            </div>
          )}

          <hr style={{ border: 'none', borderTop: '1px solid var(--borda)', margin: '18px 0' }} />

          <div className="gps-capture">
            <label className="gps-capture-label">Nova captura (leituras múltiplas)</label>
            {gpsFix ? (
              <>
                <div className={`gps-fix${gpsFix.accuracy > GPS_MAX_ACCURACY_M ? ' warn' : ''}`}>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Latitude (mediana)</label>
                      <strong>{gpsFix.latitude.toFixed(6)}</strong>
                    </div>
                    <div className="detail-item">
                      <label>Longitude (mediana)</label>
                      <strong>{gpsFix.longitude.toFixed(6)}</strong>
                    </div>
                    <div className="detail-item">
                      <label>Precisão (mediana)</label>
                      <strong>
                        {Math.round(gpsFix.accuracy)} m
                        {gpsFix.accuracy <= 5 ? ' — excelente' :
                         gpsFix.accuracy <= GPS_MAX_ACCURACY_M ? ' — aceitável' :
                         ' — reprovada para cadastro automático'}
                      </strong>
                    </div>
                    <div className="detail-item">
                      <label>Leituras</label>
                      <strong>{gpsFix.count} em até 45 s</strong>
                    </div>
                    <div className="detail-item span-2">
                      <label>Horário</label>
                      <strong>{gpsFix.at}</strong>
                    </div>
                  </div>
                </div>
                {gpsFix.accuracy > GPS_MAX_ACCURACY_M && (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '10px 0', fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={gpsForce}
                      onChange={(e) => setGpsForce(e.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      Forçar salvamento mesmo com precisão baixa (fica marcado como <strong>baixa precisão</strong> — não recomendado para cadastro automático).
                    </span>
                  </label>
                )}
                <iframe
                  className="gps-map"
                  title="Prévia do ponto capturado"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${gpsFix.longitude - 0.0004},${gpsFix.latitude - 0.0004},${gpsFix.longitude + 0.0004},${gpsFix.latitude + 0.0004}&layer=mapnik&marker=${gpsFix.latitude},${gpsFix.longitude}`}
                />
                <a
                  className="gps-open-link"
                  href={`https://maps.google.com/?q=${gpsFix.latitude},${gpsFix.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Abrir no Google Maps ↗
                </a>
              </>
            ) : gpsBusy && gpsProgress ? (
              <p className="gps-hint">
                Coletando leituras… <strong>{gpsProgress.count}</strong> recebida(s)
                {gpsProgress.accuracy != null && <> — última precisão <strong>{Math.round(gpsProgress.accuracy)} m</strong></>}
                . <br />
                Mantenha o aparelho parado, com o braço erguido, ao ar livre.
                {gpsProgress.extended
                  ? ' Sinal fraco — estendendo a janela em mais 30 segundos.'
                  : ' Até 10 leituras ou 45 segundos.'}
              </p>
            ) : (
              <p className="gps-hint">
                Toque em “Capturar minha posição”. O sistema coletará várias leituras e usará a mediana —
                mais estável que uma leitura única. O limite de precisão é {GPS_MAX_ACCURACY_M} m.
              </p>
            )}
            {gpsError && <p className="gps-error">{gpsError}</p>}
            <button className="btn-secondary" style={{ marginTop: 12 }} onClick={capturarGps} disabled={gpsBusy}>
              {gpsBusy ? `Capturando… (${gpsProgress?.count || 0} leituras)` : gpsFix ? 'Recapturar' : 'Capturar minha posição'}
            </button>
          </div>
        </Modal>
      )}

      {viewing && (
        <Modal
          title={`Jazigo ${viewing.number}`}
          onClose={() => setViewing(null)}
          footer={<button className="btn-secondary" onClick={() => setViewing(null)}>Fechar</button>}
        >
          <div className="detail-grid">
            <div className="detail-item">
              <label>Quadra</label>
              <strong>{viewing.block}</strong>
            </div>
            <div className="detail-item">
              <label>Setor</label>
              <strong>{viewing.sector || '—'}</strong>
            </div>
            <div className="detail-item">
              <label>Número</label>
              <strong>{viewing.number}</strong>
            </div>
            <div className="detail-item">
              <label>Contrato</label>
              <strong>{viewing.contract || '—'}</strong>
            </div>
            <div className="detail-item span-2">
              <label>Proprietário</label>
              <strong>{viewing.owner_name || '—'}</strong>
            </div>
            <div className="detail-item">
              <label>Status</label>
              <StatusBadge status={viewing.status} />
            </div>
            <div className="detail-item">
              <label>Posição no mapa (%)</label>
              <strong>{viewing.x_position}%, {viewing.y_position}%</strong>
            </div>
          </div>
          {viewing.latitude != null && (
            <div style={{ marginTop: 16 }}>
              <span className="gps-badge ok">✓ GPS</span>
              <span className="gps-saved-meta" style={{ marginLeft: 8 }}>
                Capturado em {fmtDateTime(viewing.captured_at)}
                {viewing.captured_by_email ? ` — ${viewing.captured_by_email}` : ''}
              </span>
              <div className="detail-grid" style={{ marginTop: 10 }}>
                <div className="detail-item">
                  <label>Latitude</label>
                  <strong>{Number(viewing.latitude).toFixed(6)}</strong>
                </div>
                <div className="detail-item">
                  <label>Longitude</label>
                  <strong>{Number(viewing.longitude).toFixed(6)}</strong>
                </div>
                <div className="detail-item">
                  <label>Precisão</label>
                  <strong>{viewing.gps_accuracy_m != null ? `${Math.round(viewing.gps_accuracy_m)} m` : '—'}</strong>
                </div>
                <div className="detail-item">
                  <label></label>
                  <a
                    href={`https://maps.google.com/?q=${viewing.latitude},${viewing.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Abrir no mapa ↗
                  </a>
                </div>
              </div>
            </div>
          )}
          <hr style={{ border: 'none', borderTop: '1px solid var(--borda)', margin: '18px 0' }} />
          {(viewing.deceased || []).length === 0 && (
            <div className="detail-item">
              <label>Sepultado</label>
              <strong>Nenhum sepultado vinculado</strong>
            </div>
          )}
          {(viewing.deceased || []).map((p, i) => (
            <div key={p.id} style={i > 0 ? { marginTop: 20 } : undefined}>
              <div className="detail-item" style={{ marginBottom: 10 }}>
                <label>Sepultado{(viewing.deceased || []).length > 1 ? ` ${i + 1}` : ''}</label>
                <strong>{p.name}</strong>
              </div>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Nascimento</label>
                  <strong>{fmtDate(p.birth_date)}</strong>
                </div>
                <div className="detail-item">
                  <label>Falecimento</label>
                  <strong>{fmtDate(p.death_date)}</strong>
                </div>
                {p.obs && (
                  <div className="detail-item span-2">
                    <label>Obs</label>
                    <strong>{p.obs}</strong>
                  </div>
                )}
              </div>
            </div>
          ))}
        </Modal>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
